import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Card, CardBody, Button, Badge } from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import { useProfile } from "../../Components/Hooks/UserHooks";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import {
  getOverdueInvoices as onGetOverdueInvoices,
  // sendManualEmail
} from "../../slices/thunks";

const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const selectInvoiceState = (state) => state.Invoices || {};
const selectInvoiceProperties = createSelector(
  selectInvoiceState,
  (invoice) => ({
    overdueData: invoice.overdueData || { reminder_invoices: [], overdue_invoices: [], summary: {} },
    loading: invoice.loading || false,
    error: invoice.error || null
  })
);

const InvoicesOverdueTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { overdueData, loading, error } = useSelector(selectInvoiceProperties);
  const { userProfile } = useProfile();
  const [activeView, setActiveView] = useState('all'); // 'all', 'reminder', 'overdue'

  const isAdmin = userProfile?.user?.groups?.some((group) =>
    ["Admin", "Editor"].includes(group)
  );

  useEffect(() => {
    fetchInvoicesOverdue();
  }, []);

  const fetchInvoicesOverdue = async () => {
    try {
      await dispatch(onGetOverdueInvoices());
    } catch (error) {
      console.error("Failed to fetch overdue invoices:", error);
    }
  };

  // Combine and filter data based on active view
  const tableData = useMemo(() => {
    const allInvoices = [
      ...(overdueData.reminder_invoices || []).map(inv => ({ ...inv, category: 'reminder' })),
      ...(overdueData.overdue_invoices || []).map(inv => ({ ...inv, category: 'overdue' }))
    ];

    let filteredInvoices = allInvoices;
    if (activeView === 'reminder') {
      filteredInvoices = allInvoices.filter(inv => inv.category === 'reminder');
    } else if (activeView === 'overdue') {
      filteredInvoices = allInvoices.filter(inv => inv.category === 'overdue');
    }

    return filteredInvoices.map((invoice) => {
      const dueDateObj = invoice.due_date ? new Date(invoice.due_date) : null;
      
      return {
        ...invoice,
        due_date_obj: dueDateObj,
        formattedDueDate: dueDateObj ? dueDateObj.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) : "—",
        days_info: invoice.email_status === 'overdue' 
          ? `${invoice.days_overdue} days overdue`
          : `Due in ${invoice.days_until_due} days`
      };
    });
  }, [overdueData, activeView]);

  const handleRowClick = useCallback((row) => {
    navigate(`/invoices/${row.original.invoice_id}`);
  }, [navigate]);

  const handleSingleEmail = async (invoice, type) => {
    // TODO: Implement when sendManualEmail is ready
    // try {
    //   await dispatch(sendManualEmail({
    //     invoiceId: invoice.invoice_id,
    //     emailType: type
    //   })).unwrap();
      
    //   console.log(`${type} email sent successfully for invoice ${invoice.series_name}/${invoice.number}`);
    //   fetchInvoicesOverdue();
    // } catch (error) {
    //   console.error(`Failed to send ${type} email:`, error);
    // }
    
    console.log(`Would send ${type} email for invoice ${invoice.series_name}/${invoice.number}`);
  };

  const columns = useMemo(() => [
    {
      header: "Invoice",
      accessorKey: "number",
      enableColumnFilter: false,
      cell: ({ getValue, row }) => (
        <div>
          <div className="fw-semibold">{row.original.series_name}/{getValue()}</div>
        </div>
      ),
    },
    {
      header: "Client",
      accessorKey: "client.client_name",
      enableColumnFilter: false,
      cell: ({ getValue, row }) => {
        const clientName = getValue() || "—";
        const clientEmail = row.original.client?.client_email;
        
        return (
          <div>
            <div className="fw-medium">{clientName}</div>
            {clientEmail && (
              <small className="text-muted">{clientEmail}</small>
            )}
          </div>
        );
      },
    },
    {
      header: () => <div className="text-center">Status</div>,
      accessorKey: "email_status",
      enableColumnFilter: false,
      cell: ({ getValue, row }) => {
        const status = getValue();
        const isOverdue = status === 'overdue';
        
        return (
          <div className="text-center">
            <Badge 
              color={isOverdue ? 'danger' : 'warning'} 
              className="mb-1 px-3 py-1"
            >
              {isOverdue ? 'Overdue' : 'Due Soon'}
            </Badge>
            <div>
              <small className="text-muted">{row.original.days_info}</small>
            </div>
          </div>
        );
      },
    },
    {
      header: () => <div className="text-center">Due Date</div>,
      accessorKey: "due_date_obj",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div className="text-center">
          <div className="fw-medium">{row.original.formattedDueDate}</div>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.due_date_obj;
        const dateB = rowB.original.due_date_obj;
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return dateA.getTime() - dateB.getTime();
      },
    },
    {
      header: () => <div className="text-center">Last Email</div>,
      accessorKey: "last_email",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const invoice = row.original;
        const reminderDate = invoice.reminder_email_set_at ? new Date(invoice.reminder_email_set_at) : null;
        const overdueDate = invoice.overdue_email_set_at ? new Date(invoice.overdue_email_set_at) : null;
        
        let lastDate = null;
        let emailType = '';
        
        if (reminderDate && overdueDate) {
          if (reminderDate > overdueDate) {
            lastDate = reminderDate;
            emailType = 'Reminder';
          } else {
            lastDate = overdueDate;
            emailType = 'Overdue';
          }
        } else if (reminderDate) {
          lastDate = reminderDate;
          emailType = 'Reminder';
        } else if (overdueDate) {
          lastDate = overdueDate;
          emailType = 'Overdue';
        }
        
        if (lastDate) {
          return (
            <div className="text-center">
              <div className="fw-medium">
                {lastDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </div>
              <Badge color="light" className="text-muted">
                {emailType}
              </Badge>
            </div>
          );
        }
        
        return (
          <div className="text-center">
            <span className="text-muted">Never sent</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      enableColumnFilter: false,
      header: () => <div className="text-center">Actions</div>,
      cell: ({ row }) => {
        if (!isAdmin) return null;
        
        const invoice = row.original;
        const isOverdue = invoice.email_status === 'overdue';
        
        return (
          <div className="text-center">
            <div className="btn-group" role="group">
              <Button
                size="sm"
                color={isOverdue ? "danger" : "warning"}
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  handleSingleEmail(invoice, invoice.email_status);
                }}
                title={`Send ${invoice.email_status} email`}
              >
                <i className={`ri-mail-line me-1`}></i>
                {isOverdue ? 'Overdue' : 'Reminder'}
              </Button>
            </div>
          </div>
        );
      },
    },
  ], [isAdmin, navigate]);

  const totalInvoices = (overdueData.summary?.reminders_count || 0) + (overdueData.summary?.overdue_count || 0);

  return (
    <Card className="shadow-sm">
      <CardBody className="pt-2">
        {/* Quick Stats */}
        {/* <Row className="g-3 mb-4">
          <Col md={4}>
            <div className="border rounded p-3 text-center bg-light">
              <div className="text-warning">
                <i className="ri-time-line fs-1"></i>
              </div>
              <h4 className="mt-2 mb-1">{overdueData.summary?.reminders_count || 0}</h4>
              <p className="text-muted mb-0">Due Soon</p>
            </div>
          </Col>
          <Col md={4}>
            <div className="border rounded p-3 text-center bg-light">
              <div className="text-danger">
                <i className="ri-alarm-warning-line fs-1"></i>
              </div>
              <h4 className="mt-2 mb-1">{overdueData.summary?.overdue_count || 0}</h4>
              <p className="text-muted mb-0">Overdue</p>
            </div>
          </Col>
          <Col md={4}>
            <div className="border rounded p-3 text-center bg-light">
              <div className="text-primary">
                <i className="ri-mail-line fs-1"></i>
              </div>
              <h4 className="mt-2 mb-1">{totalInvoices}</h4>
              <p className="text-muted mb-0">Total Invoices</p>
            </div>
          </Col>
        </Row> */}

        {/* Filter Buttons */}
        <div className="mb-4">
          <div className="btn-group" role="group">
            <input
              type="radio"
              className="btn-check"
              name="viewFilter"
              id="all"
              checked={activeView === 'all'}
              onChange={() => setActiveView('all')}
            />
            <label className="btn btn-outline-primary" htmlFor="all">
              All ({totalInvoices})
            </label>

            <input
              type="radio"
              className="btn-check"
              name="viewFilter"
              id="reminder"
              checked={activeView === 'reminder'}
              onChange={() => setActiveView('reminder')}
            />
            <label className="btn btn-outline-warning" htmlFor="reminder">
              Due Soon ({overdueData.summary?.reminders_count || 0})
            </label>

            <input
              type="radio"
              className="btn-check"
              name="viewFilter"
              id="overdue"
              checked={activeView === 'overdue'}
              onChange={() => setActiveView('overdue')}
            />
            <label className="btn btn-outline-danger" htmlFor="overdue">
              Overdue ({overdueData.summary?.overdue_count || 0})
            </label>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <TableContainer
            columns={columns}
            data={tableData}
            isGlobalFilter={true}
            isAddclient={false}
            customPageSize={10}
            handleRowClick={handleRowClick}
            customTableRowProps={(row) => ({
              className: "cursor-pointer hover-row",
              style: { cursor: "pointer" }
            })}
            divClass="table-responsive table-card"
            tableClass="align-middle table-nowrap"
            theadClass="table-light"
            SearchPlaceholder="Search invoices..."
          />
        )}

        {tableData.length === 0 && !loading && (
          <div className="text-center py-5">
            <div className="text-muted">
              <i className="ri-inbox-line fs-1 d-block mb-3"></i>
              <h5>No invoices found</h5>
              <p>No {activeView === 'all' ? '' : activeView + ' '}invoices require attention at this time.</p>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default InvoicesOverdueTable;